import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { 
  Loader2, CheckCircle2, BookOpen, FileText, Target, ArrowLeft, 
  Upload, Image, FileType, X, Sparkles 
} from "lucide-react";
import { createManagedRequest } from "@/utils/requestManager";

const PrelimsCAConverterPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const currentRequestRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState("");
  const [result, setResult] = useState(null);
  const [inputMode, setInputMode] = useState("text"); // "text" or "file"
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const isPro = user?.role === "PRO";

  // PRO Access Check - Redirect FREE users to upgrade page
  useEffect(() => {
    if (user && !isPro) {
      toast({
        variant: "destructive",
        title: "PRO Feature",
        description: "CA → MCQ Converter is a PRO feature. Please upgrade to access."
      });
      navigate("/upgrade");
    }
  }, [user, isPro, navigate, toast]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentRequestRef.current) {
        currentRequestRef.current.cancel();
      }
    };
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'text/plain'];
      if (!validTypes.includes(file.type)) {
        toast({
          variant: "destructive",
          description: "Unsupported file type. Please upload PDF, PNG, JPG, WEBP, or TXT files."
        });
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          variant: "destructive",
          description: "File too large. Maximum size is 10MB."
        });
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleConvertFile = async () => {
    if (!selectedFile) {
      toast({
        variant: "destructive",
        description: "Please select a file to upload"
      });
      return;
    }

    // Cancel any existing request
    if (currentRequestRef.current) {
      currentRequestRef.current.cancel();
    }
    
    const managedRequest = createManagedRequest('ca-converter');
    currentRequestRef.current = managedRequest;

    setLoading(true);
    setResult(null);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await managedAxios.post(
        `${API_BASE}/converter/ca-to-mcq/upload`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          signal: managedRequest.signal,
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      setResult(response.data);
      managedRequest.cleanup();
      toast({
        description: `Generated ${response.data.mcqs?.length || 0} MCQs from ${selectedFile.name}!`
      });
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Failed to convert file to MCQs"
      });
    } finally {
      setLoading(false);
      setUploadProgress(0);
      currentRequestRef.current = null;
    }
  };

  const handleConvertText = async () => {
    if (!content.trim()) {
      toast({
        variant: "destructive",
        description: "Please paste some current affairs content"
      });
      return;
    }

    // Cancel any existing request
    if (currentRequestRef.current) {
      currentRequestRef.current.cancel();
    }
    
    const managedRequest = createManagedRequest('ca-converter');
    currentRequestRef.current = managedRequest;

    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.post(
        `${API_BASE}/prelims/ca-to-mcq`,
        {
          content
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: managedRequest.signal
        }
      );

      setResult(response.data);
      managedRequest.cleanup();
      toast({
        description: `Generated ${response.data.mcqs?.length || 0} MCQs from CA content!`
      });
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Failed to convert CA to MCQs"
      });
    } finally {
      setLoading(false);
      currentRequestRef.current = null;
    }
  };

  const handleConvert = () => {
    if (inputMode === "file") {
      handleConvertFile();
    } else {
      handleConvertText();
    }
  };

  const sampleCA = `India-EU Trade Agreement Update (December 2024)

India and the European Union are advancing negotiations for a comprehensive Free Trade Agreement (FTA) that could significantly reshape bilateral trade relations. Key highlights:

• Trade Volume: Bilateral trade currently stands at €116 billion (2023)
• Tariff Reduction: Agreement aims to eliminate tariffs on 90% of goods over 10 years
• Services Sector: Focus on IT, financial services, and professional services liberalization
• Investment Protection: Enhanced provisions for investor protection and dispute settlement
• Sustainability: Inclusion of climate action and labor standards chapters
• Timeline: Negotiations expected to conclude by mid-2025

Strategic Significance:
- Diversification from China for EU supply chains
- Market access to 450 million EU consumers for Indian businesses
- Technology transfer opportunities in clean energy and digital sectors
- Strengthening India's position in Indo-Pacific economic architecture

Challenges:
- Agricultural subsidies remain contentious
- Data localization requirements under debate
- Intellectual property rights alignment needed
- Political sensitivities around labor and environmental standards`;

  const getFileIcon = (file) => {
    if (!file) return <Upload className="w-8 h-8" />;
    if (file.type.startsWith('image/')) return <Image className="w-8 h-8 text-blue-500" />;
    if (file.type === 'application/pdf') return <FileType className="w-8 h-8 text-red-500" />;
    return <FileText className="w-8 h-8 text-slate-500" />;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      {/* Back Button */}
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">CA → MCQ Converter</h1>
          <p className="text-sm text-slate-600">
            Convert current affairs content into UPSC-style MCQs
          </p>
        </div>
        <Badge className={isPro ? "bg-amber-500" : "bg-slate-200"}>
          {isPro ? "⭐ PRO" : "🔓 FREE"}
        </Badge>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <BookOpen className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">How it works:</p>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• <strong>Upload files</strong> (PDF, images) or paste text from newspapers, magazines</li>
                <li>• CLARIAS extracts factual items and converts them to UPSC-style MCQs</li>
                <li>• {isPro ? "PRO: Get 5-25 MCQs with elimination logic, PYQ links, and mains questions" : "FREE: Get 3 simple MCQs with basic explanations"}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <Card>
          <CardContent className="p-6 space-y-4">
            {/* Input Mode Toggle */}
            <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
              <button
                onClick={() => setInputMode("text")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  inputMode === "text" 
                    ? "bg-white shadow text-slate-900" 
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Paste Text
              </button>
              <button
                onClick={() => setInputMode("file")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  inputMode === "file" 
                    ? "bg-white shadow text-slate-900" 
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Upload className="w-4 h-4 inline mr-2" />
                Upload File
              </button>
            </div>

            {/* Text Input Mode */}
            {inputMode === "text" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="content">Current Affairs Content</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setContent(sampleCA)}
                    disabled={loading}
                  >
                    Load Sample
                  </Button>
                </div>
                <Textarea
                  id="content"
                  placeholder="Paste current affairs content here (news articles, policy updates, economic data, etc.)"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  disabled={loading}
                  rows={16}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500 mt-2">
                  {content.length} characters • Max 30,000 • {content.length > 0 ? `Suggested MCQs: ${Math.max(5, Math.min(25, Math.floor(content.length / 1000)))}` : ''}
                </p>
              </div>
            )}

            {/* File Upload Mode */}
            {inputMode === "file" && (
              <div>
                <Label className="mb-2 block">Upload CA Document</Label>
                
                {/* Drop Zone */}
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                    selectedFile 
                      ? "border-blue-400 bg-blue-50" 
                      : "border-slate-300 hover:border-blue-400 hover:bg-slate-50"
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={loading}
                  />
                  
                  {selectedFile ? (
                    <div className="space-y-3">
                      <div className="w-16 h-16 mx-auto bg-white rounded-xl shadow-sm flex items-center justify-center">
                        {getFileIcon(selectedFile)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{selectedFile.name}</p>
                        <p className="text-sm text-slate-500">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-16 h-16 mx-auto bg-slate-100 rounded-xl flex items-center justify-center">
                        <Upload className="w-8 h-8 text-slate-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-700">Click to upload or drag & drop</p>
                        <p className="text-sm text-slate-500">PDF, PNG, JPG, WEBP, or TXT (max 10MB)</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Upload Progress */}
                {loading && uploadProgress > 0 && (
                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-slate-600 mb-1">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-600">
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    <strong>Tip:</strong> Upload screenshots of newspaper articles or magazine pages. 
                    CLARIAS will extract the text and generate MCQs automatically!
                  </p>
                </div>
              </div>
            )}

            <Button
              onClick={handleConvert}
              disabled={loading || (inputMode === "file" && !selectedFile) || (inputMode === "text" && !content.trim())}
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-600"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {inputMode === "file" ? "Processing File..." : "Converting..."}
                </>
              ) : (
                <>
                  <Target className="w-4 h-4 mr-2" />
                  Convert to MCQs
                </>
              )}
            </Button>

            {!isPro && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-900">
                  🔒 FREE: 3 MCQs only. Upgrade to PRO for 5-25 MCQs with elimination logic, PYQ links, and mains questions.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Output Section */}
        <div className="space-y-4">
          {result && (
            <>
              {/* Source File Info */}
              {result.source_file && (
                <Card className="bg-slate-50 border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <p className="text-sm font-medium text-slate-900">
                        Extracted from: {result.source_file}
                      </p>
                    </div>
                    {result.extracted_text_preview && (
                      <p className="text-xs text-slate-500 mt-2 line-clamp-3">
                        {result.extracted_text_preview}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Summary (FREE) */}
              {result.summary && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <p className="text-sm font-semibold text-green-900 mb-1">Content Summary:</p>
                    <p className="text-sm text-slate-700">{result.summary}</p>
                  </CardContent>
                </Card>
              )}

              {/* Importance Score (PRO) */}
              {isPro && result.importance_score && (
                <Card className="bg-indigo-50 border-indigo-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-indigo-900">UPSC Relevance Score:</p>
                      <Badge className="bg-indigo-600 text-lg">
                        {result.importance_score}/100
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* MCQs */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-slate-900">
                    Generated MCQs ({result.mcqs?.length || 0})
                  </h3>
                </div>

                {result.mcqs?.map((mcq, idx) => (
                  <Card key={idx} className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4 space-y-3">
                      {/* Question */}
                      <div className="flex items-start gap-2">
                        <Badge variant="outline" className="shrink-0">Q{idx + 1}</Badge>
                        <p className="text-sm font-medium text-slate-900">{mcq.question}</p>
                      </div>

                      {/* Options */}
                      <div className="space-y-1 pl-12">
                        {Object.entries(mcq.options).map(([key, value]) => (
                          <div key={key} className="flex gap-2 text-sm">
                            <span className="font-semibold">{key})</span>
                            <span>{value}</span>
                          </div>
                        ))}
                      </div>

                      {/* Answer & Explanation */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                        <p className="text-xs font-semibold text-blue-900">
                          Answer: {mcq.correct_answer}
                        </p>
                        <p className="text-xs text-slate-700">{mcq.explanation}</p>

                        {/* PRO Features */}
                        {isPro && mcq.topic_tag && (
                          <div className="flex gap-2 pt-2 border-t border-blue-200">
                            <Badge variant="secondary" className="text-xs">
                              {mcq.topic_tag}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {mcq.difficulty}
                            </Badge>
                          </div>
                        )}

                        {isPro && mcq.pyq_link && (
                          <p className="text-xs text-slate-600 pt-2 border-t border-blue-200">
                            <span className="font-semibold">PYQ Link:</span> {mcq.pyq_link}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Mains Questions (PRO) */}
              {isPro && result.mains_questions && result.mains_questions.length > 0 && (
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4 space-y-2">
                    <p className="text-sm font-semibold text-purple-900 mb-2">
                      📝 Bonus: Mains Questions from this CA
                    </p>
                    {result.mains_questions.map((q, idx) => (
                      <div key={idx} className="text-sm text-slate-700">
                        {idx + 1}. {q}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Reset Button */}
              <Button
                onClick={() => {
                  setResult(null);
                  setContent("");
                  setSelectedFile(null);
                }}
                variant="outline"
                className="w-full"
              >
                Convert New Content
              </Button>
            </>
          )}

          {/* Placeholder */}
          {!result && !loading && (
            <Card className="border-dashed">
              <CardContent className="p-12">
                <div className="text-center text-slate-400">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Generated MCQs will appear here</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrelimsCAConverterPage;
